import { EWidgetType, toast, type IChatMessage } from '@kognitos/lattice';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import type { V1Thread, V1ThreadMessage } from '@/generated/hooks/zen/models';

import {
  useStreamingResumeMessageStream,
  useStreamingSendMessage,
} from './use-quill-streaming';
import {
  useCreateThread,
  useListThreads,
  useStopThread,
  useUpdateThread,
} from '@/generated/hooks/zen/v1/thread_service';

import { useProcessChatMessages } from '@/modules/automation-run/hooks';
import { captureException } from '@/modules/sentry';
import { isIntentionalStreamAbortError } from '@/shared/hooks/useStreamingWithCleanup';
import {
  type IQuillChatState,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  selectQuillChatAddMessages,
  selectQuillChatClearConnectionQueue,
  selectQuillChatClearProcessedPages,
  selectQuillChatSetMessages,
  selectQuillChatSetThreadId,
  selectQuillChatUpdateMessages,
  useQuillChatStore,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder, parseResourceName } from '@/shared/utils';
import { errorHandler } from '@/shared/utils/error-handler';

import {
  getExtractAutomationCodeConnectionsQueryKey,
  getGetAutomationQueryKey,
  getListDefaultInputsHistoryQueryKey,
  getQueryAutomationQueryKey,
} from '@/generated/hooks/grimoire/v1/api';
import { ChatIntegrationConnection } from '../components';
import { QUILL_AGENT_NAME } from '../constants';
import {
  type IQuillChatConnectionRequestMessage,
  type IQuillChatSelectedConnection,
  type IUseQuillThreadMessagesParams,
} from '../types';
import {
  completeActiveToolCallMessages,
  getTodoProgressMessageId,
  getConnectionResponseMessage,
  mergeConsecutiveInternalToolCallMessages,
  processIncomingThreadMessage,
  reconcileToolCallActivity,
} from '../utils';
import { useQuillThreadPreviousMessages } from './useQuillThreadPreviousMessages';

/** Delay before auto-reconnecting after stream ends gracefully (ms) */
const RECONNECT_DELAY_MS = 300;
/** Delay before surfacing long-running send-request indicator (ms) */
const LONG_REQUEST_WARNING_MS = 10_000;
/**
 * Max consecutive auto-resume attempts for a single turn before giving up.
 * Bounds recovery so a persistently failing stream surfaces a terminal error
 * (clearing the thinking indicator) instead of resuming forever. The counter
 * resets whenever a message successfully arrives or a new turn starts.
 */
const MAX_AUTO_RESUME_ATTEMPTS = 2;

const STREAM_REQUEST_TIMEOUT_MS = 120 * 1000;

const TERMINAL_STATES: ReadonlySet<string> = new Set([
  'STATE_COMPLETE',
  'STATE_ERROR',
  'STATE_CANCELLED',
]);

function isTerminalState(state: string | undefined): boolean {
  return TERMINAL_STATES.has(state ?? '');
}

function useQuillThreadMessages({
  automationId,
  onFileClick,
  onSuggestedPromptClick,
  initialMessage,
}: IUseQuillThreadMessagesParams) {
  const { t } = useTranslation('automation');

  // Use useShallow for shallow equality comparison to prevent unnecessary re-renders
  const messages = useQuillChatStore(
    useShallow(
      (state: IQuillChatState) =>
        state.chatsByAutomation[automationId]?.messages ?? [],
    ),
  );

  // Store actions (stable references)
  const updateMessages = useQuillChatStore(selectQuillChatUpdateMessages);
  const addMessages = useQuillChatStore(selectQuillChatAddMessages);
  const setMessages = useQuillChatStore(selectQuillChatSetMessages);
  const clearProcessedPages = useQuillChatStore(
    selectQuillChatClearProcessedPages,
  );
  const clearConnectionQueue = useQuillChatStore(
    selectQuillChatClearConnectionQueue,
  );
  const setThreadId = useQuillChatStore(selectQuillChatSetThreadId);

  const {
    value: isProcessingMessage,
    setTrue: setIsProcessingMessageTrue,
    setFalse: setIsProcessingMessageFalse,
  } = useBoolean(false);

  const {
    value: isSendingMessage,
    setTrue: setIsSendingMessageTrue,
    setFalse: setIsSendingMessageFalse,
  } = useBoolean(false);
  const [isLongRunningSendRequest, setIsLongRunningSendRequest] =
    useState(false);
  const [isCreatingThread, setIsCreatingThread] = useState(false);

  // Track active execution ID for browser session detection
  const [activeExecutionId, setActiveExecutionId] = useState<string | null>(
    null,
  );

  // Ref to hold reconnect function for use in onComplete callback
  const reconnectRef = useRef<(() => void) | null>(null);

  const queryClient = useQueryClient();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const automationName = `organizations/${currentOrganizationId}/workspaces/${currentWorkspaceId}/automations/${automationId}`;
  const [selectedThreadId, setSelectedThreadId] = useState<string>();

  const {
    data: threads,
    isLoading: loadingThreads,
    isError: isErrorLoadingThreads,
    refetch: refetchThreads,
  } = useListThreads(
    currentOrganizationId,
    currentWorkspaceId,
    QUILL_AGENT_NAME,
    {
      page_size: 20,
      filter: FilterBuilder.create()
        .equals('automation', automationName)
        .build(),
    },
  );

  const { mutateAsync: createThreadAsync } = useCreateThread();
  const { mutateAsync: updateThreadAsync } = useUpdateThread();

  const threadList = threads?.data?.threads ?? [];
  const latestThreadId = parseResourceName(threadList[0]?.name).thread;
  const threadId = selectedThreadId ?? latestThreadId;

  // Store threadId in the quill chat store for cleanup purposes
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync threadId to store when it changes
  useEffect(() => {
    if (automationId && threadId) {
      setThreadId(automationId, threadId);
    }
  }, [automationId, threadId, setThreadId]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Select the latest thread once thread data loads
  useEffect(() => {
    if (!selectedThreadId && latestThreadId) {
      setSelectedThreadId(latestThreadId);
    }
  }, [latestThreadId, selectedThreadId]);

  // Ref to hold the resolve/reject functions for "first message received" promise
  // This allows sendMessageToThread to wait for acknowledgment before returning
  const firstMessageResolveRef = useRef<{
    resolve: () => void;
    reject: (error: Error) => void;
  } | null>(null);

  const shouldResumeStream = useCallback(
    (latestMessage: V1ThreadMessage | undefined): boolean =>
      !!latestMessage && !isTerminalState(latestMessage.state),
    [],
  );

  // Ref to store the latest message for stream recovery checks
  const latestMessageRef = useRef<V1ThreadMessage | undefined>(undefined);
  // Consecutive auto-resume attempts for the current turn. Reset on a new turn
  // and whenever a message successfully arrives; capped by MAX_AUTO_RESUME_ATTEMPTS.
  const resumeAttemptsRef = useRef(0);
  const activeTodoTurnIdRef = useRef<string | undefined>(undefined);
  const activeTodoMessageIdRef = useRef<string | undefined>(undefined);

  /**
   * Resolves the stable todo-progress message ID for the active user turn.
   * Computes and caches the ID on first access within a turn so that
   * subsequent todo_progress artifacts in the same flush reuse it.
   */
  const resolveActiveTodoMessageId = (fallbackName?: string): string => {
    if (activeTodoMessageIdRef.current) {
      return activeTodoMessageIdRef.current;
    }
    const turnId = activeTodoTurnIdRef.current ?? fallbackName ?? 'active-turn';
    const id = getTodoProgressMessageId(turnId);
    activeTodoMessageIdRef.current = id;
    return id;
  };

  // --- Batched message processing ---
  // During rapid streaming, many events can arrive within a single animation
  // frame. Instead of triggering separate state updates (and re-renders) for
  // each event, we buffer incoming events and flush them once per frame.
  // This collapses N per-event renders into 1 render per ~16ms window.
  const pendingEventsRef = useRef<V1ThreadMessage[]>([]);
  const rafIdRef = useRef<number | null>(null);

  /**
   * Flushes all buffered events into a single consolidated state update.
   * Processes messages and makes one batch of state changes.
   */
  const flushPendingEvents = useCallback(() => {
    rafIdRef.current = null;
    const events = pendingEventsRef.current;
    if (events.length === 0) return;
    pendingEventsRef.current = [];

    let newActiveExecutionId: string | null = null;
    let processingState: boolean | null = null;
    const chatMessages: IChatMessage[] = [];

    for (const event of events) {
      if (
        (event.user_message || event.state === 'STATE_QUEUED') &&
        event.name
      ) {
        activeTodoTurnIdRef.current = event.name;
        activeTodoMessageIdRef.current = getTodoProgressMessageId(event.name);
      }

      if (
        event.artifact?.artifact_type === 'execution_reference' &&
        event.artifact.content
      ) {
        try {
          const executionRef = JSON.parse(event.artifact.content);
          if (executionRef.execution_id) {
            newActiveExecutionId = executionRef.execution_id;
          }
        } catch {
          // Ignore parse errors
        }
      }

      const chatMessage = processIncomingThreadMessage({
        event,
        onConnectionSelect: sendConnectionResponse,
        onAutomationBuilt: invalidateAutomationsQuery,
        onFileClick: handleFileClick,
        onAddConnection: handleAddConnection,
        onDefaultInputsReceived: invalidateAutomationsQuery,
        threadId,
        automationId,
      });

      if (isTerminalState(event.state)) {
        processingState = false;
      } else if (event.state === 'STATE_QUEUED') {
        processingState = true;
      }

      if (chatMessage) {
        if (event.artifact?.artifact_type === 'todo_progress') {
          chatMessages.push({
            ...chatMessage,
            id: resolveActiveTodoMessageId(event.name),
          });
        } else {
          chatMessages.push(chatMessage);
        }
      }
    }

    // Single batch of state updates — React 18 batches these into one render
    if (newActiveExecutionId !== null) {
      setActiveExecutionId(newActiveExecutionId);
    }
    if (processingState !== null) {
      if (processingState) {
        setIsProcessingMessageTrue();
      } else {
        setIsProcessingMessageFalse();
        updateMessages(automationId, completeActiveToolCallMessages);
      }
    }
    if (automationId && chatMessages.length > 0) {
      if (processingState !== false) {
        updateMessages(automationId, completeActiveToolCallMessages);
      }
      const messagesToAdd =
        processingState === false
          ? completeActiveToolCallMessages(chatMessages)
          : reconcileToolCallActivity(chatMessages);
      const lastExistingMessage = messages[messages.length - 1];
      const mergedMessages = mergeConsecutiveInternalToolCallMessages(
        lastExistingMessage
          ? [lastExistingMessage, ...messagesToAdd]
          : messagesToAdd,
      );
      addMessages(automationId, mergedMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are stable refs resolved at call time
  }, [
    automationId,
    addMessages,
    updateMessages,
    setIsProcessingMessageFalse,
    setIsProcessingMessageTrue,
    messages,
    threadId,
  ]);

  /**
   * Handles a single incoming message from the stream.
   * Buffers the event and schedules a flush on the next animation frame.
   * The first-message promise is resolved immediately to avoid delaying
   * the "sending → processing" UI transition.
   */
  const handleIncomingMessage = useCallback(
    (event: V1ThreadMessage) => {
      // Resolve first-message promise immediately so the UI
      // can transition from "sending" to "processing" without waiting for RAF
      if (firstMessageResolveRef.current) {
        firstMessageResolveRef.current.resolve();
        firstMessageResolveRef.current = null;
      }

      pendingEventsRef.current.push(event);
      latestMessageRef.current = event;
      // A successful event means the stream is healthy again; restore the
      // full auto-resume budget for any later mid-turn disconnect.
      resumeAttemptsRef.current = 0;

      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(flushPendingEvents);
      }
    },
    [flushPendingEvents],
  );

  // Clean up pending RAF on unmount
  // eslint-disable-next-line no-restricted-syntax -- reason: Cancel pending requestAnimationFrame on unmount to prevent stale flush
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  /**
   * Terminal failure path for a turn: stop the thinking indicator and surface
   * the error so the user can retry, instead of resuming forever. Mirrors the
   * cleanup in sendMessageToThread's catch block.
   *
   * @param error - The error that occurred
   * @param actionName - Name of the action for Sentry tracking
   */
  const failProcessing = useCallback(
    (error: Error, actionName: string) => {
      resumeAttemptsRef.current = 0;
      if (firstMessageResolveRef.current) {
        firstMessageResolveRef.current.reject(error);
        firstMessageResolveRef.current = null;
      }
      setIsProcessingMessageFalse();
      setIsSendingMessageFalse();
      errorHandler(error, () => {
        captureException(error, {
          tags: {
            component: 'useQuillThreadMessages',
            action: actionName,
          },
        });
      });
    },
    [setIsProcessingMessageFalse, setIsSendingMessageFalse],
  );

  const handleStreamError = useCallback(
    (error: Error, actionName: string) => {
      // Don't handle abort errors (intentional cancellation)
      if (isIntentionalStreamAbortError(error)) {
        return;
      }

      // Only auto-resume while the turn is genuinely mid-flight and we still
      // have recovery budget. Otherwise give up: clear the thinking indicator
      // and surface the error rather than spinning indefinitely.
      //
      // A turn is resumable when the latest message is not yet terminal, OR
      // when no message has arrived at all (latestMessageRef is undefined). The
      // no-message case is the primary Saks scenario — a NetworkError before
      // the first event — and must still consume its retry budget rather than
      // failing immediately. shouldResumeStream(undefined) returns false (it
      // guards reconnect-on-complete), so we check the no-message case here.
      const latest = latestMessageRef.current;
      const isTurnResumable = !latest || shouldResumeStream(latest);

      // Turn already reached a terminal state; a stream error here is just
      // post-completion connection teardown, not a real failure. Exit silently
      // rather than logging spurious Sentry noise.
      if (!isTurnResumable) {
        return;
      }

      const canResume = resumeAttemptsRef.current < MAX_AUTO_RESUME_ATTEMPTS;
      if (!canResume) {
        failProcessing(error, actionName);
        return;
      }

      resumeAttemptsRef.current += 1;
      setTimeout(() => {
        reconnectRef.current?.();
      }, RECONNECT_DELAY_MS);

      errorHandler(error, () => {
        captureException(error, {
          tags: {
            component: 'useQuillThreadMessages',
            action: actionName,
          },
        });
      });
    },
    [failProcessing, shouldResumeStream],
  );

  /**
   * Handles stream completion and auto-reconnects if processing is ongoing.
   */
  const handleStreamComplete = useCallback(() => {
    // Check if we need to resume (processing not finished)
    if (shouldResumeStream(latestMessageRef.current)) {
      setTimeout(() => {
        reconnectRef.current?.();
      }, RECONNECT_DELAY_MS);
    }
  }, [shouldResumeStream]);

  // Resume stream hook - for reconnection after disconnects or on mount
  const {
    resumeMessageStream,
    cancelCurrentStream: cancelResumeStream,
    isLoading: isResumeStreamActive,
  } = useStreamingResumeMessageStream({
    timeoutMs: STREAM_REQUEST_TIMEOUT_MS,
    onMessage: handleIncomingMessage,
    onComplete: handleStreamComplete,
    onError: error => handleStreamError(error, 'resumeMessageStream'),
  });

  // Send message hook - for sending new messages with streaming response
  // maxRetries controls total attempts (not retries-after-first). Setting to 1
  // means a single attempt with no retries. sendMessage is not idempotent — each
  // call creates a new user message on the backend, so retrying on timeout would
  // duplicate the message. Recovery is handled by resumeMessageStream instead.
  const {
    sendMessage: streamingSendMessage,
    cancelCurrentStream: cancelSendStream,
    isLoading: isSendStreamActive,
  } = useStreamingSendMessage({
    maxRetries: 1,
    onMessage: handleIncomingMessage,
    onComplete: handleStreamComplete,
    onError: error => handleStreamError(error, 'sendMessage'),
    timeoutMs: STREAM_REQUEST_TIMEOUT_MS,
  });

  /**
   * Cancels all active streams.
   */
  const stopListeningToThread = useCallback(() => {
    cancelResumeStream();
    cancelSendStream();
  }, [cancelResumeStream, cancelSendStream]);

  const resetThreadView = useCallback(
    (nextThreadId: string | undefined) => {
      stopListeningToThread();
      pendingEventsRef.current = [];
      latestMessageRef.current = undefined;
      resumeAttemptsRef.current = 0;
      setActiveExecutionId(null);
      activeTodoTurnIdRef.current = undefined;
      activeTodoMessageIdRef.current = undefined;
      setIsProcessingMessageFalse();
      setIsSendingMessageFalse();
      setMessages(automationId, []);
      clearProcessedPages(automationId);
      clearConnectionQueue(automationId);
      setThreadId(automationId, nextThreadId);
    },
    [
      automationId,
      clearConnectionQueue,
      clearProcessedPages,
      setIsProcessingMessageFalse,
      setIsSendingMessageFalse,
      setMessages,
      setThreadId,
      stopListeningToThread,
    ],
  );

  const selectThread = useCallback(
    (nextThreadId: string) => {
      if (!nextThreadId || nextThreadId === threadId) {
        return;
      }
      setSelectedThreadId(nextThreadId);
      resetThreadView(nextThreadId);
    },
    [resetThreadView, threadId],
  );

  const createNewThread = useCallback(async () => {
    if (isCreatingThread) {
      return;
    }

    setIsCreatingThread(true);
    try {
      const response = await createThreadAsync({
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        agent: QUILL_AGENT_NAME,
        data: {
          automation: automationName,
        },
      });
      const nextThreadId = parseResourceName(response.data.name).thread;
      setSelectedThreadId(nextThreadId);
      resetThreadView(nextThreadId);
      await refetchThreads();
    } catch (error) {
      errorHandler(error as Error, () => {
        captureException(error as Error, {
          tags: {
            component: 'useQuillThreadMessages',
            action: 'createNewThread',
          },
        });
        toast.error(t('automation.quillChat.error.createThread'));
      });
    } finally {
      setIsCreatingThread(false);
    }
  }, [
    automationName,
    createThreadAsync,
    currentOrganizationId,
    currentWorkspaceId,
    isCreatingThread,
    refetchThreads,
    resetThreadView,
    t,
  ]);

  const renameThread = useCallback(
    async (nextDisplayName: string) => {
      if (!threadId) {
        return;
      }

      try {
        await updateThreadAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: automationId,
          agent: QUILL_AGENT_NAME,
          thread: threadId,
          data: {
            automation: automationName,
            display_name: nextDisplayName.trim(),
          },
        });
      } catch (error) {
        errorHandler(error as Error, () => {
          captureException(error as Error, {
            tags: {
              component: 'useQuillThreadMessages',
              action: 'renameThread',
            },
          });
          toast.error(t('automation.quillChat.error.renameThread'));
        });
        throw error;
      }
      // Rename already committed; refresh the list in the background. A refetch
      // failure must not toast a rename error or revert the title in EditableTitle.
      void refetchThreads();
    },
    [
      automationId,
      automationName,
      currentOrganizationId,
      currentWorkspaceId,
      refetchThreads,
      t,
      threadId,
      updateThreadAsync,
    ],
  );

  /**
   * Resumes the message stream.
   * Called from onPreviousMessagesLoaded when the latest message is not in terminal state,
   * and retried on stream errors if the message is still not terminal.
   */
  const resumeStream = useCallback(async () => {
    if (!threadId) return;

    try {
      // Cancel any existing streams before resuming
      stopListeningToThread();

      await resumeMessageStream(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
        QUILL_AGENT_NAME,
        threadId,
        {},
      );
    } catch (error) {
      handleStreamError(error as Error, 'resumeStream');
    }
  }, [
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    handleStreamError,
    resumeMessageStream,
    stopListeningToThread,
    threadId,
  ]);

  // Keep reconnect ref in sync with resumeStream — must be inside useEffect
  // so that the ref is only updated after React commits, avoiding stale closures
  // when setTimeout in handleStreamError fires between renders.
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync ref with latest resumeStream callback after each render commit
  useEffect(() => {
    reconnectRef.current = resumeStream;
  }, [resumeStream]);

  // Combined stream active state
  const isStreamConnected = isResumeStreamActive || isSendStreamActive;

  /**
   * Sends a message to the thread using the streaming API.
   * The stream handles both sending and receiving responses.
   * Returns a promise that resolves when the first message is received from the stream,
   * allowing the UI to clear the input after acknowledgment.
   *
   * @param params - The parameters object
   * @param params.message - The thread message to send
   * @param params.action - Action name for error tracking in Sentry (defaults to 'sendMessageToThread')
   * @returns Promise that resolves when the first message is received
   */
  const sendMessageToThread = useCallback(
    async ({
      message,
      action,
    }: {
      message: V1ThreadMessage;
      action?: string;
    }): Promise<void> => {
      if (!threadId && isErrorLoadingThreads) {
        return;
      }

      const longRequestTimer = setTimeout(() => {
        setIsLongRunningSendRequest(true);
      }, LONG_REQUEST_WARNING_MS);

      // For feedback messages, we don't want to set isProcessingMessage or isSendingMessage
      if (!message.user_feedback) {
        // Clear stale execution ID so browser session hook discards old data.
        // A new execution_reference artifact will set the correct ID when it arrives.
        setActiveExecutionId(null);
        activeTodoTurnIdRef.current = undefined;
        activeTodoMessageIdRef.current = undefined;
        resumeAttemptsRef.current = 0;
        setIsProcessingMessageTrue();
        setIsSendingMessageTrue();
      }

      let targetThreadId = threadId;

      try {
        if (!threadId && !isErrorLoadingThreads) {
          const response = await createThreadAsync({
            organization: currentOrganizationId,
            workspace: currentWorkspaceId,
            agent: QUILL_AGENT_NAME,
            data: {
              automation: automationName,
            },
          });
          refetchThreads();
          targetThreadId = parseResourceName(response.data.name).thread ?? '';
          setSelectedThreadId(targetThreadId);
          setThreadId(automationId, targetThreadId);
        }

        if (!targetThreadId) {
          return;
        }

        // Create promise and immediately pass handlers to stream to avoid race condition
        await new Promise<void>((resolve, reject) => {
          firstMessageResolveRef.current = { resolve, reject };

          streamingSendMessage(
            currentOrganizationId,
            currentWorkspaceId,
            automationId,
            QUILL_AGENT_NAME,
            targetThreadId!,
            { user_message: message },
          ).catch(reject); // Errors flow through the promise
        });

        // After receiving first message, mark sending as complete
        setIsSendingMessageFalse();
      } catch (error) {
        // Cleanup on error
        if (firstMessageResolveRef.current) {
          firstMessageResolveRef.current = null;
        }
        setIsProcessingMessageFalse();
        setIsSendingMessageFalse();
        errorHandler(error as Error, () => {
          captureException(error as Error, {
            tags: {
              component: 'useQuillThreadMessages',
              method: 'sendMessageToThread',
              action,
            },
          });
        });
        throw error;
      } finally {
        clearTimeout(longRequestTimer);
        setIsLongRunningSendRequest(false);
      }
    },
    [
      threadId,
      isErrorLoadingThreads,
      setIsProcessingMessageTrue,
      setIsSendingMessageTrue,
      setIsSendingMessageFalse,
      createThreadAsync,
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      automationName,
      refetchThreads,
      setThreadId,
      streamingSendMessage,
      setIsProcessingMessageFalse,
      setIsLongRunningSendRequest,
    ],
  );

  const sendConnectionResponse = async (
    connection: IQuillChatSelectedConnection,
    connectionRequest: IQuillChatConnectionRequestMessage,
    messageId: string,
  ) => {
    const connectionMessage = getConnectionResponseMessage(
      {
        book_name: connectionRequest.book_name,
        book_version: connectionRequest.book_version,
        connection_id: connection.connection_id,
      },
      messageId,
    );
    await sendMessageToThread({
      message: connectionMessage,
      action: 'sendConnectionResponse',
    });
  };

  const handleFileClick = (file: string) => {
    onFileClick?.(file);
  };

  const invalidateAutomationsQuery = (defaultInputsUpdated?: boolean) => {
    if (defaultInputsUpdated) {
      queryClient.invalidateQueries({
        queryKey: getListDefaultInputsHistoryQueryKey(
          currentOrganizationId,
          currentWorkspaceId,
          automationId,
        ),
      });
    }
    queryClient.invalidateQueries({
      queryKey: getQueryAutomationQueryKey(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getGetAutomationQueryKey(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getExtractAutomationCodeConnectionsQueryKey(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
      ),
    });
  };

  /**
   * Updates an existing connection_request message with the selected connectionId.
   * This is called when an add_connection message is received.
   */
  const handleAddConnection = (
    targetMessageId: string,
    connectionId: string,
    bookName: string,
    bookVersion: string,
  ) => {
    if (!automationId) return;
    updateMessages(automationId, prev =>
      prev.map(msg => {
        if (
          msg.id === targetMessageId &&
          msg.widget?.type === EWidgetType.CUSTOM
        ) {
          return {
            ...msg,
            widget: {
              ...msg.widget,
              customContent: (
                <ChatIntegrationConnection
                  integration_name={bookName}
                  integration_version={bookVersion}
                  connectionId={connectionId}
                />
              ),
            },
          };
        }
        return msg;
      }),
    );
  };

  // Ref to track scroll container for preserving scroll position
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const {
    isLoading: loadingPreviousMessages,
    isFetchingMore: isFetchingMorePreviousMessages,
    fetchNextPage,
    hasNextPage,
    previousMessagesData,
  } = useQuillThreadPreviousMessages({
    automationId,
    threadId,
    onConnectionSelect: sendConnectionResponse,
    onFileClick: handleFileClick,
    existingMessages: messages,
    setIsProcessingMessageTrue,
    setIsProcessingMessageFalse,
    invalidateAutomationsQuery,
    currentOrganizationId,
    currentWorkspaceId,
    scrollContainerRef,
    onPreviousMessagesLoaded: (latestMessage: V1ThreadMessage | undefined) => {
      // Update latest message ref with the most recent message from history
      if (latestMessage && latestMessage.name) {
        if (latestMessage.state === 'STATE_QUEUED') {
          setIsProcessingMessageTrue();
        }
        latestMessageRef.current = latestMessage;
      }
      // Resume stream if the latest message is not in terminal state
      if (shouldResumeStream(latestMessage)) {
        resumeStream();
      }
    },
    // Restore active execution ID from previous messages on page refresh
    // This allows browser session detection to work after a page reload
    onExecutionIdFound: (executionId: string) => {
      setActiveExecutionId(executionId);
    },
  });

  // Send the initial message (from navigation state) once threads have loaded.
  // Guards (each must hold before we send):
  //  1. initialMessage is set.
  //  2. threads list has finished loading.
  //  3. We haven't already sent in this mount.
  //  4. previousMessages query is not currently loading.
  //  5. If a thread already exists for this automation, previousMessagesData
  //     must be defined. Without this, the query being briefly disabled or
  //     errored (which both leave isLoading=false / data=undefined) would
  //     make `hasAnyPriorUserMessage` return false and re-issue the initial
  //     send on a QuillChat remount — the "first user message appears twice"
  //     regression.
  //  6. `hasAnyPriorUserMessage` is false (no prior user message in this
  //     thread's history).
  const initialMessageSentRef = useRef(false);
  // eslint-disable-next-line no-restricted-syntax -- reason: Send initial message once after threads are loaded and no prior user messages exist
  useEffect(() => {
    if (!initialMessage || loadingThreads || initialMessageSentRef.current) {
      return;
    }

    if (loadingPreviousMessages) {
      return;
    }

    if (threadId && !previousMessagesData) {
      return;
    }

    const hasAnyPriorUserMessage =
      previousMessagesData?.pages.some(page =>
        page.data.thread_messages?.some(msg => msg.user_message),
      ) ?? false;

    if (!hasAnyPriorUserMessage) {
      initialMessageSentRef.current = true;
      sendMessageToThread({
        message: initialMessage,
        action: 'sendInitialMessage',
      });
    }
  }, [
    initialMessage,
    loadingThreads,
    loadingPreviousMessages,
    previousMessagesData,
    threadId,
    sendMessageToThread,
  ]);

  const { mutate: stopThread } = useStopThread({
    mutation: {
      onSuccess: () => {
        setIsProcessingMessageFalse();
      },
      onError: error => {
        errorHandler(error, () => {
          captureException(error as Error, {
            tags: {
              component: 'useQuillThreadMessages',
              action: 'stopThread',
            },
          });
          toast.error(t('automation.quillChat.error.stopThread'));
        });
      },
    },
  });

  const stopProcessingPreviousMessages = () => {
    if (threadId) {
      stopThread({
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        agent: QUILL_AGENT_NAME,
        thread: threadId,
        data: {},
      });
    }
  };

  const processedMessages = useProcessChatMessages({
    messages,
    exceptionFields: [],
    onSuggestedPromptClick: onSuggestedPromptClick
      ? prompt => onSuggestedPromptClick(prompt, sendMessageToThread)
      : undefined,
  });

  return {
    messages: processedMessages,
    isSendingMessage,
    isLongRunningSendRequest,
    isProcessingMessage,
    isStreamConnected,
    loadingPreviousMessages,
    isFetchingMorePreviousMessages,
    loadingThreads,
    sendMessageToThread,
    resumeStream,
    stopListeningToThread,
    stopProcessingPreviousMessages,
    fetchPreviousPage: fetchNextPage,
    hasMorePreviousMessages: hasNextPage,
    scrollContainerRef,
    activeExecutionId,
    threads: threadList as V1Thread[],
    selectedThreadId: threadId,
    selectThread,
    createNewThread,
    renameThread,
    isCreatingThread,
  };
}

export { useQuillThreadMessages };
